import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {connect} from 'pwa-helpers';
import {
	Scene,
	WebGLRenderer,
	PerspectiveCamera,
	MeshBasicMaterial,
	Sprite,
	SpriteMaterial,
	Mesh,
	PlaneGeometry,
	DoubleSide,
	Vector2,
	Shape,
	ShapeGeometry,
	Box3,
	Vector3,
	TextureLoader,
	Raycaster
} from 'three';
import {TextGeometry} from 'three/examples/jsm/geometries/TextGeometry.js';
import {FontLoader} from 'three/examples/jsm/loaders/FontLoader.js';
import SunCalc from 'suncalc';
import store from '../../store';
import {temperatureToDecimal} from '../../util/temperatureToDecimal';
import {clearThreeObj} from '../../util/clearThreeObj';
import {lightProfiles} from '../lightProfiles';
import {selectProfileId, selectSpcConfiguration} from '../../selectors';
import {setSelectedDay} from '../lightProfileSlice';

@customElement('light-profile-day')
export class LightProfileDay extends connect(store)(LitElement) {

	static styles = css`
		:host {
			display: flex;
		}

		canvas, img {
			height: 100%;
			width: auto;
			margin: auto;
		}

		@media (max-width: 768px) {
			canvas, img {
				max-width: 60vw;
				max-height: 20vh;
				display: block;
			}
		}
	`;

	scene = new Scene();
	sceneBack = new Scene();
	sceneSprites = new Scene();

	renderer = new WebGLRenderer({alpha: true, antialias: true});
	camera = new PerspectiveCamera(35, 2 / 1, 1, 500);
	raycaster = new Raycaster();
	pointer = new Vector2();
	hoveredTime = null;
	connected = false;
	sunriseSprite = null;
	sunsetSprite = null;
	timemarkSprite = null;

	backMaterial = new MeshBasicMaterial({
		color: 0x303030,
		side: DoubleSide,
		wireframe: false,
		opacity: 1,
		transparent: false
	});
	selectedMaterial = new MeshBasicMaterial({
		color: 0xff0000,
		side: DoubleSide,
		wireframe: false,
		opacity: 1,
		transparent: false
	});

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());

		this.renderer = new WebGLRenderer({alpha: true, antialias: true});
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.setSize(600, 300);

		this.camera.position.set(0, 20, 80);
		this.camera.lookAt(0, -20, 0);
		this.camera.layers.enableAll();

		this.sunTexture = new TextureLoader().load('/line-assets/img/sun.png');
		this.timemarkTexture = new TextureLoader().load('/line-assets/img/timemark.png');
		this.loadFont();
		this.renderGraphicsSprites();
	}

	connectedCallback() {
		super.connectedCallback();
		this.connected = true;

		this.renderGraphics();
		this.renderGraphicsBack();

		this.animate();

		window.addEventListener('resize', () => this.onWindowResize());
		window.addEventListener('pointermove', (e) => this.onPointerMove(e));
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.connected = false;
		console.log('lpd disconnected');

		clearThreeObj(this.scene);
		this.scene.clear();
		this.renderer.renderLists.dispose();
		this.renderer.dispose();
		window.removeEventListener('resize', () => this.onWindowResize());
		window.removeEventListener('pointermove', (e) => this.onPointerMove(e));
	}

	stateChanged(state) {
		this.state = state;
		this.language = state.status.lang;
		this.translations = state.productData.translations;
		this.spcConfiguration = selectSpcConfiguration(state);

		this.lightProfile = lightProfiles.find(profile => profile.id === selectProfileId(state));
		this.renderChannel = this.lightProfile?.renderChannels.find(rc => rc.id === state.lightProfile.data.channelId);
		if (this.renderChannel) {
			this.layers = this.renderChannel.layers;
			this.camera.layers.enableAll();
			//this.camera.layers.disableAll();
			//this.camera.layers.enable(0);
			//this.layers.forEach(layer => this.camera.layers.enable(layer + 1));
		} else {
			this.camera.layers.enableAll();
		}
		if (this.lightProfile?.type !== 'dualOrthogonal') {
			this.camera.layers.disable(5);
		}
		this.dailySegments = state.lightProfile.data.dailySegments;
		if (this.selectedSecond !== state.lightProfile.data.selectedSecond) {
			this.showSelectedSecond(state.lightProfile.data.selectedSecond);
		}
		if (this.simulationIndex !== state.lightProfile.data.simulationIndex || this.selectedDay !== state.lightProfile.data.selectedDay) {
			this.simulationIndex = state.lightProfile.data.simulationIndex;
			this.selectedDay = state.lightProfile.data.selectedDay;
			this.renderGraphics();

			// update sun sprites
			const date = new Date(2024, 0, this.selectedDay, 12, 0, 0);
			const suntime = SunCalc.getTimes(date, this.state.lightProfile.data.project.location.latitude, this.state.lightProfile.data.project.location.longitude);
			const sunrise = Date.parse(suntime.sunrise.toLocaleString('en-US', {
				dateStyle: 'medium',
				timeStyle: 'medium',
				timeZone: this.state.lightProfile.data.project.location.timezoneName
			})) - date.getTime() + (1000 * 60 * 60 * 12);
			const sunset = Date.parse(suntime.sunset.toLocaleString('en-US', {
				dateStyle: 'medium',
				timeStyle: 'medium',
				timeZone: this.state.lightProfile.data.project.location.timezoneName
			})) - date.getTime() - (1000 * 60 * 60 * 12);

			if (this.sunriseSprite) {
				this.sunriseSprite.position.set(63 * sunrise / (1000 * 60 * 60 * 24), -8.8, 0);
				this.sunsetSprite.position.set(80 * sunset / (1000 * 60 * 60 * 24), -11.3, 0);
			}
		}


	}

	loadFont() {
		const loader = new FontLoader();
		loader.load('/line-assets/three-fonts/droid/droid_sans_mono_regular.typeface.json', (response) => {
			this.font = response;
			this.loadTexture();
			this.renderGraphicsBack();
		});
	}

	loadTexture() {
		const loader = new TextureLoader();
		loader.load('/line-assets/alphagradient.png', (response) => {
			this.gradientTexture = response;
			this.renderGraphics();
		});
	}

	paintHourBack(quarterHour) {
		const geometry = new PlaneGeometry(0.2, 20);
		const width = 72;
		const horizontalPlane = new Mesh(geometry, this.backMaterial);
		horizontalPlane.position.x = (quarterHour * 0.25 * (3 * (23 / 24))) - width / 2;
		horizontalPlane.position.y = -10.1;
		horizontalPlane.position.z = 10;
		horizontalPlane.rotateX(90 * Math.PI / 180);
		horizontalPlane.name = '' + quarterHour;
		horizontalPlane.layers.enable(0); // place on layer 1

		this.sceneBack.add(horizontalPlane);

		const vpgeometry = new PlaneGeometry(0.2, 13);
		const verticalPlane = new Mesh(vpgeometry, this.backMaterial);
		verticalPlane.position.x = (quarterHour * 0.25 * (3 * (23 / 24))) - width / 2;
		verticalPlane.position.y = -4.5;
		verticalPlane.position.z = -3;
		//verticalPlane.rotateX(90 * Math.PI / 180);

		verticalPlane.rotateX(Math.PI * -0.15);
		verticalPlane.name = '' + quarterHour;
		verticalPlane.layers.set(5); // place on layer 5

		this.sceneBack.add(verticalPlane);


		const materialHit = new MeshBasicMaterial({
			color: 0x000000,
			side: DoubleSide,
			wireframe: false,
			opacity: 0,
			transparent: true
		});
		const geometryHit = new PlaneGeometry(1, 20);

		const horizontalPlaneHit = new Mesh(geometryHit, materialHit);
		horizontalPlaneHit.position.x = (quarterHour * 0.25 * (3 * (23 / 24))) - width / 2;
		horizontalPlaneHit.position.y = -10.1;
		horizontalPlaneHit.position.z = 10;
		horizontalPlaneHit.rotateX(90 * Math.PI / 180);
		horizontalPlaneHit.name = '' + quarterHour + 'Highlight';
		horizontalPlaneHit.layers.enable(0); // place on layer 1
		this.sceneBack.add(horizontalPlaneHit);

		// labels
		this.addText('HH', -41.5, -13, 20, 'left');
		this.addText('12:00', -37.5, -13, 20, 'left');
		this.addText('24:00', 0, -13, 20, 'center');
		this.addText('12:00', 34.5, -13, 20, 'right');
	}

	addText(str, x, y, z, align = 'left') {
		if (this.font) {
			const material = new MeshBasicMaterial({
				color: 0x909090,
				side: DoubleSide,
				wireframe: false,
				opacity: 1,
				transparent: false
			});
			const label = new TextGeometry(str, {
				font: this.font,
				size: 1.3,
				depth: 0,
				curveSegments: 5,
				bevelEnabled: false
			});
			const labelMesh = new Mesh(label, material);

			const bb = new Box3().setFromObject(labelMesh);
			const labelSize = new Vector3();
			bb.getSize(labelSize);
			const width = bb.max.x - bb.min.x;
			let offset;
			switch (align) {
				case 'center':
					offset = -width / 2;
					break;
				case 'right':
					offset = -width;
					break;
				default:
					offset = 0;
			}
			labelMesh.position.x = x + offset;
			labelMesh.position.y = y;
			labelMesh.position.z = z;
			labelMesh.rotateX(-30 * Math.PI / 180);
			labelMesh.layers.enable(0);
			this.sceneBack.add(labelMesh);
		}
	}

	paintDay(day, segments, layer) {

		const segmentsForDay = segments.days[day];
		const layerColor = this.lightProfile.layers[layer].color;
		const layerDimming = this.lightProfile.layers[layer].dimming;
		const type = this.lightProfile.type;

		if (segmentsForDay && segmentsForDay.length > 0) {
			segmentsForDay.forEach((segment, index) => {

				const start = segment.start * 72;
				const width = segment.size * 72;
				let height = 20;
				if (type === 'dualFlat') {
					height = layer === 0 ? 20 : 10;
				}
				if (type === 'dualOrthogonal') {
					if (layer === 1) height = 13;

				}

				const hasFade = segment.brightness !== segment.prevBrightness;

				let color = layerColor;
				if (segment.cct) {
					color = temperatureToDecimal(segment.cct);
				}

				const squareShape = new Shape()
					.moveTo(0, 0)
					.lineTo(0, height)
					.lineTo(width, height)
					.lineTo(width, 0)
					.lineTo(0, 0);
				const geometry = new ShapeGeometry(squareShape);
				if (hasFade) {
					this.fadeUVS(geometry.attributes.uv, 1 - segment.prevBrightness, 1 - segment.brightness);
				}
				const material = new MeshBasicMaterial({
					color: color,
					side: DoubleSide,
					wireframe: false,
					opacity: segment.brightness,
					transparent: true
				});
				if (hasFade) {
					material.opacity = 1;
					material.alphaMap = this.gradientTexture;
				}
				const segmentPlane = new Mesh(geometry, material);
				segmentPlane.position.x = start - 72 / 2;
				segmentPlane.position.y = -10.5;
				if (type === 'dualFlat') {
					segmentPlane.position.y = -10.5 + layer * 0.5;
				}
				if (type === 'dualOrthogonal') {
					if (layer === 1) segmentPlane.rotateX((Math.PI) * -0.15);
					if (layer === 0) segmentPlane.rotateX((Math.PI / 2));
				} else {
					segmentPlane.rotateX((Math.PI / 2));
				}

				segmentPlane.layers.set(layer + 1);
				this.scene.add(segmentPlane);
			});
		}
	}

	fadeUVS(uvs, pin, pout) {
		uvs.setXY(0, pin, pin);
		uvs.setXY(1, pin, pin);
		uvs.setXY(2, pout, pout);
		uvs.setXY(3, pout, pout);
		uvs.needsUpdate = true;
	}

	showSelectedSecond(second) {
		/*
		const quarterHour = Math.floor(second/(60*15));
		const oldObject = this.sceneBack.getObjectByName('' + Math.floor(this.selectedSecond/(60*15)));
		const newObject = this.sceneBack.getObjectByName('' + quarterHour);
		this.selectedSecond = second;
		if (oldObject) oldObject.material = this.backMaterial;
		if (newObject) newObject.material = this.selectedMaterial;
		*/
		const dayRange = second / (24 * 60 * 60);
		const width = 69;
		this.timemarkSprite.position.set(-width / 2 + (width * dayRange) - 1, -6.5, 0);
		this.threeRender();
	}

	animate() {
		if (!this.connected) return;
		this.raycaster.setFromCamera(this.pointer, this.camera);
		this.raycaster.layers.set(0);
		const intersected = this.raycaster.intersectObjects(this.sceneBack.children);
		for (let i = 0; i < intersected.length; i++) {
			if (intersected[i].object.name.indexOf('Highlight') === -1 && this.hoveredTime !== intersected[i].object.name) {
				this.hoveredTime = Number.parseInt(intersected[i].object.name, 10);
				if (this.state.lightProfile.data.selectedSecond !== this.hoveredTime / 4 * (60 * 60)) {
					store.dispatch(setSelectedDay({
						selectedDay: this.selectedDay,
						selectedSecond: this.hoveredTime / 4 * (60 * 60)
					}));
				}
			}
		}
		//this.renderer.render(this.scene, this.camera);
		requestAnimationFrame(() => this.animate());
	}

	renderGraphicsBack() {
		if (!this.connected) return;
		clearThreeObj(this.sceneBack);
		this.sceneBack.clear();
		for (let i = 0; i <= 24 * 4; i++) {
			this.paintHourBack(i);
		}
		this.showSelectedSecond(this.selectedSecond);
	}

	renderGraphics() {
		if (!this.connected) return;
		clearThreeObj(this.scene);
		if (this.dailySegments) {
			this.dailySegments.forEach((segments, index) => {
				this.paintDay(this.selectedDay, segments, index);
			});
		}
		this.threeRender();
	}

	renderGraphicsSprites() {
		//if (!this.connected) return;
		//clearThreeObj(this.sceneSprites);

		const timemarkMaterial = new SpriteMaterial({map: this.timemarkTexture});
		this.timemarkSprite = new Sprite(timemarkMaterial);
		this.timemarkSprite.position.set(0, -6.5, 0);
		this.timemarkSprite.scale.set(1.3, 4.33 * 1.3, 1);

		const sunriseMaterial = new SpriteMaterial({map: this.sunTexture, rotation: Math.PI});
		this.sunriseSprite = new Sprite(sunriseMaterial);
		this.sunriseSprite.position.set(20, -8.5, 0);
		this.sunriseSprite.scale.set(5, 2.5, 1);

		const sunsetMaterial = new SpriteMaterial({map: this.sunTexture});
		this.sunsetSprite = new Sprite(sunsetMaterial);
		this.sunsetSprite.position.set(-20, -11.5, 0);
		this.sunsetSprite.scale.set(5, 2.5, 1);

		this.sceneSprites.add(this.timemarkSprite);
		this.sceneSprites.add(this.sunriseSprite);
		this.sceneSprites.add(this.sunsetSprite);
		this.threeRender();
	}

	onWindowResize() {
		// this.camera.aspect = window.innerWidth / window.innerHeight;
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(600, 300);
		this.threeRender();
	}

	onPointerMove(event) {
		const rect = this.renderer.domElement.getBoundingClientRect();
		const width = rect.right - rect.left;
		const height = rect.bottom - rect.top;
		this.pointer.x = (event.clientX - rect.left - width / 2) / width * 2;
		this.pointer.y = -(event.clientY - rect.top - height / 2) / height * 2;
	}

	threeRender() {
		this.renderer.autoClear = true;
		this.renderer.render(this.sceneBack, this.camera);
		this.renderer.autoClear = false;
		this.renderer.clearDepth();
		this.renderer.render(this.scene, this.camera);
		this.renderer.clearDepth();
		this.renderer.render(this.sceneSprites, this.camera);
	}

	render() {
		const s = this.state;
		return html`
			${this.renderer.domElement}
		`;
	}
}
